import { useState, useRef, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Loader2, MessageSquare, Bug, Lightbulb, Wand2, AlertTriangle,
  ThumbsUp, ChevronLeft, ChevronRight, Link2, X, Sparkles, Wrench, Mail, Paperclip, FileText, ImageIcon,
} from "lucide-react";

// ─── Constantes (sentinelle "all", jamais "") ────────────────────────────────

const STATUT_TABS = [
  { value: "all", label: "Tous" },
  { value: "nouveau", label: "Nouveau" },
  { value: "accepte", label: "Accepté" },
  { value: "refuse", label: "Refusé" },
  { value: "livre", label: "Livré" },
];

const SOURCES = [
  { value: "all", label: "Toutes les sources" },
  { value: "jobotech", label: "Jobotech" },
  { value: "portail_partenaire", label: "Portail partenaire" },
  { value: "portail_client", label: "Portail client" },
];

const TYPES_FILTER = [
  { value: "all", label: "Tous les types" },
  { value: "bug", label: "Bug" },
  { value: "suggestion", label: "Suggestion" },
  { value: "amelioration_ux", label: "Amélioration UX" },
  { value: "idee", label: "Idée" },
  { value: "autre", label: "Autre" },
];

const MODULES = [
  { value: "all", label: "Tous les modules" },
  { value: "missions", label: "Missions" },
  { value: "chat_sav", label: "Chat SAV" },
  { value: "portail_partenaires", label: "Portail partenaires" },
  { value: "portail_clients", label: "Portail clients" },
  { value: "contacts", label: "Contacts" },
  { value: "sync_zoho", label: "Sync Zoho" },
  { value: "sync_gcal", label: "Sync GCal" },
  { value: "pdf", label: "PDF" },
  { value: "sous_traitance", label: "Sous-traitance" },
  { value: "dooh", label: "DOOH" },
  { value: "autre", label: "Autre" },
];

const PRIORITES_FILTER = [
  { value: "all", label: "Toutes les priorités" },
  { value: "haute", label: "Haute" },
  { value: "moyenne", label: "Moyenne" },
  { value: "basse", label: "Basse" },
];

const STATUTS_EDIT = [
  { value: "nouveau", label: "Nouveau" },
  { value: "accepte", label: "Accepté" },
  { value: "refuse", label: "Refusé" },
  { value: "livre", label: "Livré" },
  { value: "en_cours", label: "En cours" },
  { value: "planifie", label: "Planifié" },
  { value: "traite", label: "Traité" },
  { value: "rejete", label: "Rejeté" },
];

const PRIORITES_EDIT = [
  { value: "haute", label: "🔴 Haute" },
  { value: "moyenne", label: "🟡 Moyenne" },
  { value: "basse", label: "🟢 Basse" },
  { value: "normale", label: "Normale" },
];

const ALLOWED_MIME = ["image/png", "image/jpeg", "image/webp", "application/pdf"];
const MAX_SIZE = 10 * 1024 * 1024;

// ─── Helpers ────────────────────────────────────────────────────────────────

function typeIcon(t: string) {
  if (t === "bug") return <Bug className="w-3.5 h-3.5 text-red-500" />;
  if (t === "idee") return <Lightbulb className="w-3.5 h-3.5 text-yellow-500" />;
  if (t === "amelioration_ux") return <Wand2 className="w-3.5 h-3.5 text-purple-500" />;
  return <MessageSquare className="w-3.5 h-3.5 text-blue-500" />;
}

function statutColor(s: string): string {
  const map: Record<string, string> = {
    nouveau: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    accepte: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    refuse: "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300",
    livre: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
    en_cours: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
    planifie: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
    traite: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    rejete: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
  };
  return map[s] ?? "bg-secondary text-secondary-foreground";
}

function prioriteColor(p: string): string {
  if (p === "haute") return "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300";
  if (p === "moyenne") return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300";
  if (p === "basse") return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
  return "bg-secondary text-secondary-foreground";
}

function origineLabel(o: string): string {
  const map: Record<string, string> = { jobotech: "Jobotech", portail_partenaire: "Portail", portail_client: "Client" };
  return map[o] ?? o;
}

function typeLabel(t: string): string {
  const map: Record<string, string> = { bug: "Bug", suggestion: "Suggestion", amelioration_ux: "UX", idee: "Idée", autre: "Autre" };
  return map[t] ?? t;
}

function moduleLabel(m: string | null): string | null {
  if (!m) return null;
  const map: Record<string, string> = {
    missions: "Missions", chat_sav: "Chat SAV", portail_partenaires: "Portail partners",
    portail_clients: "Portail clients", contacts: "Contacts", sync_zoho: "Sync Zoho",
    sync_gcal: "Sync GCal", pdf: "PDF", sous_traitance: "Sous-traitance", dooh: "DOOH", autre: "Autre",
  };
  return map[m] ?? m;
}

interface FeedbackAttachment {
  id: string;
  feedbackId: string;
  chemin: string;
  filename: string;
  contentType: string;
  createdAt: string;
  url: string | null;
}

interface FeedbackItem {
  id: string;
  numero: string | null;
  titre: string | null;
  message: string;
  type: string;
  origine: string;
  statut: string;
  priorite: string | null;
  moduleLabel: string | null;
  auteurNom: string | null;
  auteurEmail: string | null;
  contexteUrl: string | null;
  pieceJointeUrl: string | null;
  demandeParenteId: string | null;
  decisionMotif: string | null;
  noteInterne: string | null;
  emetteurNotifieAt: string | null;
  derniereNotifStatut: string | null;
  iaTitre: string | null;
  iaResume: string | null;
  iaTypeSuggere: string | null;
  iaModuleSuggere: string | null;
  iaSousDemandes: { titre: string; description: string }[] | null;
  iaTraiteAt: string | null;
  createdAt: string;
  voteCount: number;
  doublonsCount: number;
  reponseAttachments?: FeedbackAttachment[];
}

// ─── Composant principal ─────────────────────────────────────────────────────

export default function AdminFeedbackBoard() {
  const { toast } = useToast();
  const qc = useQueryClient();

  // Filtres — tous initialisés à "all" (jamais "")
  const [activeTab, setActiveTab] = useState("all");
  const [source, setSource] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [prioriteFilter, setPrioriteFilter] = useState("all");
  const [tri, setTri] = useState("recent");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  // Panneau détail
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editStatut, setEditStatut] = useState("nouveau");
  const [editPriorite, setEditPriorite] = useState("normale");
  const [editModule, setEditModule] = useState("all");
  const [editMotif, setEditMotif] = useState("");
  const [editNote, setEditNote] = useState("");
  const [doublon, setDoublon] = useState("");
  const [doublonSearch, setDoublonSearch] = useState("");
  const [notifierEmetteur, setNotifierEmetteur] = useState(true);

  // PJ de réponse — état local upload en cours
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─── Queries ────────────────────────────────────────────────────────────────

  const params = new URLSearchParams({ page: String(page), tri });
  if (source !== "all") params.set("source", source);
  if (typeFilter !== "all") params.set("type", typeFilter);
  if (activeTab !== "all") params.set("statut", activeTab);
  if (moduleFilter !== "all") params.set("module_label", moduleFilter);
  if (prioriteFilter !== "all") params.set("priorite", prioriteFilter);

  const { data, isLoading } = useQuery<{ items: FeedbackItem[]; total: number }>({
    queryKey: ["/api/feedback", source, typeFilter, activeTab, moduleFilter, prioriteFilter, tri, page],
    queryFn: () => authFetch(`/api/feedback?${params}`).then(r => r.json()),
  });

  const { data: detail } = useQuery<FeedbackItem & { votants: string[] }>({
    queryKey: ["/api/feedback", selectedId],
    queryFn: () => authFetch(`/api/feedback/${selectedId}`).then(r => r.json()),
    enabled: !!selectedId,
  });

  function openDetail(item: FeedbackItem) {
    setSelectedId(item.id);
    setEditStatut(item.statut || "nouveau");
    setEditPriorite(item.priorite || "normale");
    setEditModule(item.moduleLabel || "all");
    setEditMotif(item.decisionMotif || "");
    setEditNote(item.noteInterne || "");
    setDoublon("");
    setDoublonSearch("");
    setNotifierEmetteur(true); // case cochée par défaut à chaque ouverture
  }

  // ─── Upload PJ de réponse ────────────────────────────────────────────────────

  const uploadAttachment = useCallback(async (file: File, feedbackId: string) => {
    if (!ALLOWED_MIME.includes(file.type)) {
      toast({ title: "Format non autorisé", description: "PNG, JPEG, WebP ou PDF uniquement", variant: "destructive" });
      return;
    }
    if (file.size > MAX_SIZE) {
      toast({ title: "Fichier trop lourd", description: "Maximum 10 Mo par fichier", variant: "destructive" });
      return;
    }
    setUploadingAttach(true);
    try {
      const fd = new FormData();
      fd.append("fichier", file);
      const res = await authFetch(`/api/feedback/${feedbackId}/reponse-attachments`, { method: "POST", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Erreur inconnue" }));
        throw new Error(err.message);
      }
      qc.invalidateQueries({ queryKey: ["/api/feedback", feedbackId] });
    } catch (e: any) {
      toast({ title: "Erreur upload", description: e?.message ?? "Erreur inconnue", variant: "destructive" });
    } finally {
      setUploadingAttach(false);
    }
  }, [toast, qc]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!selectedId) return;
    files.forEach(f => uploadAttachment(f, selectedId));
    e.target.value = "";
  }, [selectedId, uploadAttachment]);

  // Ctrl+V dans le panneau détail
  useEffect(() => {
    if (!selectedId) return;
    const handler = (e: ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const imageItem = items.find(i => i.type.startsWith("image/"));
      if (!imageItem) return;
      const file = imageItem.getAsFile();
      if (!file) return;
      const ext = file.type.split("/")[1] || "png";
      const named = new File([file], `paste_${Date.now()}.${ext}`, { type: file.type });
      uploadAttachment(named, selectedId);
    };
    window.addEventListener("paste", handler);
    return () => window.removeEventListener("paste", handler);
  }, [selectedId, uploadAttachment]);

  const deleteAttachMutation = useMutation({
    mutationFn: ({ feedbackId, attachId }: { feedbackId: string; attachId: string }) =>
      authFetch(`/api/feedback/${feedbackId}/reponse-attachments/${attachId}`, { method: "DELETE" }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/feedback", vars.feedbackId] });
    },
    onError: () => toast({ title: "Erreur suppression", variant: "destructive" }),
  });

  // ─── Mutations ───────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, any> }) =>
      authFetch(`/api/feedback/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(r => r.json()),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["/api/feedback"] });
      setSelectedId(null);
      if (data?.notif_sent) {
        toast({ title: "Enregistré", description: "Émetteur informé par email ✓" });
      } else if (data?.notif_error) {
        toast({ title: "Enregistré", description: `Email non envoyé : ${data.notif_error}`, variant: "destructive" });
      } else {
        toast({ title: "Feedback mis à jour" });
      }
    },
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });

  const voteMutation = useMutation({
    mutationFn: ({ id, remove }: { id: string; remove?: boolean }) =>
      authFetch(`/api/feedback/${id}/vote`, { method: remove ? "DELETE" : "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/feedback"] }),
  });

  const applyAIMutation = useMutation({
    mutationFn: (id: string) =>
      authFetch(`/api/feedback/${id}/appliquer-ia`, { method: "POST" }).then(r => r.json()),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["/api/feedback"] });
      if (detail) {
        setEditStatut(data.statut || editStatut);
        setEditModule(data.moduleLabel || editModule);
        if (data.titre) setEditMotif(editMotif);
      }
      toast({ title: "Suggestions IA appliquées" });
    },
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });

  const lierMutation = useMutation({
    mutationFn: ({ id, parent_id }: { id: string; parent_id: string | null }) =>
      authFetch(`/api/feedback/${id}/lier`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_id }),
      }).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/feedback"] });
      toast({ title: "Doublon lié" });
      setSelectedId(null);
    },
    onError: (e: any) => toast({ title: e?.message || "Erreur", variant: "destructive" }),
  });

  // ─── Filtrage + affichage ────────────────────────────────────────────────────

  const items = data?.items ?? [];
  const totalPages = data ? Math.ceil(data.total / 25) : 1;

  const filtered = search.trim()
    ? items.filter(i =>
        (i.numero ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (i.titre ?? i.message).toLowerCase().includes(search.toLowerCase()) ||
        (i.auteurNom ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (i.auteurEmail ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : items;

  const allItems = data?.items ?? [];
  const doublonCandidates = doublonSearch.trim()
    ? allItems.filter(i =>
        i.id !== selectedId &&
        !i.demandeParenteId &&
        ((i.numero ?? "").toLowerCase().includes(doublonSearch.toLowerCase()) ||
         (i.titre ?? i.message).toLowerCase().includes(doublonSearch.toLowerCase()))
      ).slice(0, 8)
    : [];

  // ─── Sauvegarde triage ───────────────────────────────────────────────────────

  function handleSave() {
    if (!selectedId) return;
    saveMutation.mutate({
      id: selectedId,
      body: {
        statut: editStatut,
        priorite: editPriorite,
        module_label: editModule !== "all" ? editModule : null,
        decision_motif: editMotif || null,
        note_interne: editNote || null,
        notifier_emetteur: notifierEmetteur,
      },
    });
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  const selectedItem = detail ?? items.find(i => i.id === selectedId) ?? null;
  const reponseAttachments: FeedbackAttachment[] = (detail as any)?.reponseAttachments ?? [];

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* En-tête */}
      <div>
        <h1 className="text-xl font-semibold text-foreground">Feedback & Roadmap</h1>
        <p className="text-sm text-muted-foreground">Triage des remontées — Jobotech · Portail partenaires · Portail clients</p>
      </div>

      {/* Onglets statut */}
      <div className="flex gap-1 bg-muted/40 p-1 rounded-lg w-fit border flex-wrap">
        {STATUT_TABS.map(t => (
          <button
            key={t.value}
            onClick={() => { setActiveTab(t.value); setPage(1); }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === t.value ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid={`tab-statut-${t.value}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher…"
          className="h-8 w-44 text-sm"
          data-testid="input-search-feedback"
        />
        <Select value={source || "all"} onValueChange={v => { setSource(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-44 text-sm" data-testid="select-source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter || "all"} onValueChange={v => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-40 text-sm" data-testid="select-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPES_FILTER.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={moduleFilter || "all"} onValueChange={v => { setModuleFilter(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-44 text-sm" data-testid="select-module">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODULES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={prioriteFilter || "all"} onValueChange={v => { setPrioriteFilter(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-44 text-sm" data-testid="select-priorite">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRIORITES_FILTER.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-1 ml-auto">
          <Button
            size="sm"
            variant={tri === "recent" ? "default" : "outline"}
            className="h-8 text-xs"
            onClick={() => setTri("recent")}
            data-testid="btn-tri-recent"
          >
            Récent
          </Button>
          <Button
            size="sm"
            variant={tri === "votes" ? "default" : "outline"}
            className="h-8 text-xs"
            onClick={() => setTri("votes")}
            data-testid="btn-tri-votes"
          >
            <ThumbsUp className="w-3 h-3 mr-1" />Votes
          </Button>
        </div>
        {data && (
          <span className="text-xs text-muted-foreground" data-testid="feedback-total">
            {data.total} entrée{data.total !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Liste */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
          <MessageSquare className="w-8 h-8 opacity-30" />
          <p className="text-sm">Aucun feedback trouvé</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => {
            const displayTitle = item.iaTitre ?? item.titre ?? item.message.substring(0, 90) + (item.message.length > 90 ? "…" : "");
            const hasMultipleSousDemandes = Array.isArray(item.iaSousDemandes) && item.iaSousDemandes.length > 1;
            return (
              <div
                key={item.id}
                className="border border-border rounded-lg px-4 py-3 hover:bg-accent/30 cursor-pointer transition-colors flex items-start gap-3"
                onClick={() => openDetail(item)}
                data-testid={`feedback-card-${item.id}`}
              >
                <div className="mt-0.5 shrink-0">{typeIcon(item.type)}</div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-start gap-2 flex-wrap">
                    {item.numero && (
                      <span className="text-[10px] font-mono text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded shrink-0" data-testid={`feedback-numero-${item.id}`}>
                        {item.numero}
                      </span>
                    )}
                    <span className="text-sm font-medium text-foreground leading-snug">{displayTitle}</span>
                    {hasMultipleSousDemandes && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 px-1.5 py-0.5 rounded-full shrink-0" data-testid={`badge-multi-${item.id}`}>
                        <AlertTriangle className="w-2.5 h-2.5" />plusieurs demandes
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                    <span className={`px-1.5 py-0.5 rounded-full font-medium ${statutColor(item.statut)}`}>{item.statut}</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{typeLabel(item.type)}</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{origineLabel(item.origine)}</span>
                    {item.moduleLabel && <span className="px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">{moduleLabel(item.moduleLabel)}</span>}
                    {item.priorite && item.priorite !== "normale" && (
                      <span className={`px-1.5 py-0.5 rounded-full font-medium ${prioriteColor(item.priorite)}`}>{item.priorite}</span>
                    )}
                    {item.doublonsCount > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                        {item.doublonsCount} doublon{item.doublonsCount > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {item.auteurNom && (
                    <p className="text-[11px] text-muted-foreground">{item.auteurNom} · {new Date(item.createdAt).toLocaleDateString("fr-FR")}</p>
                  )}
                </div>
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <button
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-md hover:bg-muted/50"
                    onClick={e => { e.stopPropagation(); voteMutation.mutate({ id: item.id }); }}
                    data-testid={`btn-vote-${item.id}`}
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                    <span className="font-semibold">{item.voteCount}</span>
                  </button>
                  {item.iaTraiteAt && <Sparkles className="w-3 h-3 text-violet-400" title="IA traitée" />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center gap-2 justify-center pt-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="btn-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="btn-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* ─── Panneau détail ─────────────────────────────────────────────────────── */}
      <Sheet open={!!selectedId} onOpenChange={open => !open && setSelectedId(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" side="right">
          {selectedItem && (
            <>
              <SheetHeader className="pb-3 border-b">
                <SheetTitle className="flex items-center gap-2 text-base">
                  {typeIcon(selectedItem.type)}
                  <span className="truncate">{selectedItem.iaTitre ?? selectedItem.titre ?? selectedItem.numero ?? "Feedback"}</span>
                </SheetTitle>
                <div className="flex gap-1.5 flex-wrap mt-1">
                  {selectedItem.numero && (
                    <span className="text-[10px] font-mono text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded">{selectedItem.numero}</span>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statutColor(selectedItem.statut)}`}>{selectedItem.statut}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{origineLabel(selectedItem.origine)}</span>
                </div>
              </SheetHeader>

              <div className="space-y-5 py-4">
                {/* Message brut */}
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Message original</p>
                  <p className="text-sm text-foreground bg-secondary/40 rounded-lg px-3 py-2.5 whitespace-pre-wrap" data-testid="detail-message">
                    {selectedItem.message}
                  </p>
                </div>

                {/* Infos auteur */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {selectedItem.auteurNom && <><span className="text-muted-foreground">Auteur</span><span className="font-medium">{selectedItem.auteurNom}</span></>}
                  {selectedItem.auteurEmail && <><span className="text-muted-foreground">Email</span><span>{selectedItem.auteurEmail}</span></>}
                  {selectedItem.contexteUrl && <><span className="text-muted-foreground">Page</span><span className="font-mono truncate">{selectedItem.contexteUrl}</span></>}
                  <span className="text-muted-foreground">Date</span><span>{new Date(selectedItem.createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</span>
                </div>

                {selectedItem.pieceJointeUrl && (
                  <a href={selectedItem.pieceJointeUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid="detail-pj">
                    <Wrench className="w-3 h-3" />Voir la capture jointe
                  </a>
                )}

                {/* Votes */}
                {(selectedItem.voteCount > 0 || (detail as any)?.votants?.length > 0) && (
                  <div className="flex items-center gap-2 text-sm">
                    <ThumbsUp className="w-4 h-4 text-muted-foreground" />
                    <span className="font-semibold">{selectedItem.voteCount}</span>
                    <span className="text-muted-foreground">vote{selectedItem.voteCount > 1 ? "s" : ""}</span>
                    {(detail as any)?.votants?.length > 0 && (
                      <span className="text-xs text-muted-foreground">— {(detail as any).votants.slice(0, 3).join(", ")}{(detail as any).votants.length > 3 ? ` +${(detail as any).votants.length - 3}` : ""}</span>
                    )}
                  </div>
                )}

                <Separator />

                {/* Bloc IA */}
                {selectedItem.iaTraiteAt && (
                  <div className="space-y-3 bg-violet-50 dark:bg-violet-900/20 rounded-xl p-4 border border-violet-200 dark:border-violet-800">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" />Suggestions IA
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-100 dark:border-violet-700 dark:text-violet-300"
                        onClick={() => applyAIMutation.mutate(selectedItem.id)}
                        disabled={applyAIMutation.isPending}
                        data-testid="btn-apply-ia"
                      >
                        {applyAIMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                        Appliquer
                      </Button>
                    </div>
                    {selectedItem.iaTitre && <p className="text-sm font-medium text-foreground">{selectedItem.iaTitre}</p>}
                    {selectedItem.iaResume && <p className="text-xs text-muted-foreground">{selectedItem.iaResume}</p>}
                    <div className="flex gap-2 flex-wrap text-[10px]">
                      {selectedItem.iaTypeSuggere && <span className="bg-white dark:bg-black/20 border rounded-full px-2 py-0.5">{selectedItem.iaTypeSuggere}</span>}
                      {selectedItem.iaModuleSuggere && <span className="bg-white dark:bg-black/20 border rounded-full px-2 py-0.5">{selectedItem.iaModuleSuggere}</span>}
                    </div>
                    {Array.isArray(selectedItem.iaSousDemandes) && selectedItem.iaSousDemandes.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-semibold text-orange-700 dark:text-orange-300 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />Plusieurs demandes détectées
                        </p>
                        {selectedItem.iaSousDemandes.map((sd, i) => (
                          <div key={i} className="bg-white dark:bg-black/20 rounded-lg px-2.5 py-2 border border-orange-200 dark:border-orange-800/50">
                            <p className="text-xs font-medium">{sd.titre}</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5">{sd.description}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Triage */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Triage</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Statut</Label>
                      <Select value={editStatut || "nouveau"} onValueChange={setEditStatut}>
                        <SelectTrigger className="h-8 text-sm" data-testid="select-edit-statut"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUTS_EDIT.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Priorité</Label>
                      <Select value={editPriorite || "normale"} onValueChange={setEditPriorite}>
                        <SelectTrigger className="h-8 text-sm" data-testid="select-edit-priorite"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PRIORITES_EDIT.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Module</Label>
                    <Select value={editModule || "all"} onValueChange={setEditModule}>
                      <SelectTrigger className="h-8 text-sm" data-testid="select-edit-module"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MODULES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Motif de décision</Label>
                    <Textarea
                      value={editMotif}
                      onChange={e => setEditMotif(e.target.value)}
                      rows={2}
                      className="text-sm resize-none"
                      placeholder="Pourquoi accepté / refusé…"
                      data-testid="textarea-motif"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Note interne</Label>
                    <Textarea
                      value={editNote}
                      onChange={e => setEditNote(e.target.value)}
                      rows={2}
                      className="text-sm resize-none"
                      placeholder="Commentaire interne…"
                      data-testid="textarea-note"
                    />
                  </div>

                  {/* Pièces jointes de réponse */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs flex items-center gap-1.5">
                        <Paperclip className="w-3 h-3" />
                        Visuels / documents joints à la réponse
                        <span className="text-muted-foreground font-normal">(inclus dans l'email)</span>
                      </Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingAttach}
                        data-testid="btn-add-attachment"
                      >
                        {uploadingAttach ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Paperclip className="w-3 h-3 mr-1" />}
                        Joindre
                      </Button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      className="hidden"
                      onChange={handleFileInputChange}
                      data-testid="input-attachment-file"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      PNG, JPEG, WebP, PDF — max 10 Mo · ou coller une image avec Ctrl+V
                    </p>

                    {/* Vignettes existantes */}
                    {reponseAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {reponseAttachments.map(a => {
                          const isImage = a.contentType.startsWith("image/");
                          return (
                            <div key={a.id} className="relative group" data-testid={`attachment-thumb-${a.id}`}>
                              {isImage && a.url ? (
                                <a href={a.url} target="_blank" rel="noreferrer">
                                  <img
                                    src={a.url}
                                    alt={a.filename}
                                    className="w-20 h-20 object-cover rounded-lg border border-border"
                                  />
                                </a>
                              ) : (
                                <a
                                  href={a.url ?? "#"}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex flex-col items-center justify-center w-20 h-20 rounded-lg border border-border bg-muted/40 text-muted-foreground hover:bg-muted/70 transition-colors gap-1 px-1"
                                >
                                  <FileText className="w-5 h-5" />
                                  <span className="text-[9px] text-center line-clamp-2 leading-tight">{a.filename}</span>
                                </a>
                              )}
                              <button
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => deleteAttachMutation.mutate({ feedbackId: selectedItem.id, attachId: a.id })}
                                title="Supprimer"
                                data-testid={`btn-delete-attachment-${a.id}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {uploadingAttach && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Upload en cours…
                      </div>
                    )}
                  </div>

                  {/* Notification émetteur */}
                  {(() => {
                    const hasEmail = !!selectedItem.auteurEmail;
                    const alreadyNotified = selectedItem.emetteurNotifieAt;
                    const motifVide = !editMotif.trim();
                    return (
                      <div className="rounded-lg border border-border p-3 bg-secondary/20 space-y-1.5">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="notifier-emetteur"
                            checked={notifierEmetteur && hasEmail}
                            disabled={!hasEmail}
                            onChange={e => setNotifierEmetteur(e.target.checked)}
                            data-testid="checkbox-notifier-emetteur"
                            className="h-4 w-4 rounded border-input accent-primary"
                          />
                          <label
                            htmlFor="notifier-emetteur"
                            className={`flex items-center gap-1.5 text-sm font-medium select-none ${hasEmail ? "cursor-pointer text-foreground" : "text-muted-foreground cursor-not-allowed"}`}
                          >
                            <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                            Informer l'émetteur
                          </label>
                        </div>
                        {!hasEmail && (
                          <p className="text-xs text-muted-foreground pl-6" data-testid="text-no-email">
                            Pas d'email pour cet émetteur
                          </p>
                        )}
                        {hasEmail && notifierEmetteur && motifVide && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 pl-6" data-testid="text-warn-motif">
                            ⚠ L'émetteur recevra le statut sans explication — ajouter un motif ?
                          </p>
                        )}
                        {hasEmail && notifierEmetteur && reponseAttachments.length > 0 && (
                          <p className="text-xs text-emerald-600 dark:text-emerald-400 pl-6 flex items-center gap-1" data-testid="text-attach-count">
                            <ImageIcon className="w-3 h-3" />
                            {reponseAttachments.length} pièce{reponseAttachments.length > 1 ? "s jointes" : " jointe"} incluse{reponseAttachments.length > 1 ? "s" : ""} dans l'email
                          </p>
                        )}
                        {alreadyNotified && (
                          <p className="text-xs text-emerald-600 dark:text-emerald-400 pl-6 flex items-center gap-1" data-testid="text-already-notified">
                            ✓ Émetteur informé le {new Date(alreadyNotified).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                            {selectedItem.derniereNotifStatut && ` (statut : ${selectedItem.derniereNotifStatut})`}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Lier comme doublon */}
                {!selectedItem.demandeParenteId ? (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                      <Link2 className="w-3.5 h-3.5" />Lier comme doublon de…
                    </p>
                    <Input
                      value={doublonSearch}
                      onChange={e => setDoublonSearch(e.target.value)}
                      placeholder="Rechercher par numéro ou titre…"
                      className="h-8 text-sm"
                      data-testid="input-doublon-search"
                    />
                    {doublonCandidates.length > 0 && (
                      <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                        {doublonCandidates.map(c => (
                          <button
                            key={c.id}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-accent/50 transition-colors"
                            onClick={() => {
                              setDoublon(c.id);
                              setDoublonSearch(c.numero ?? c.message.substring(0, 40));
                            }}
                            data-testid={`btn-doublon-select-${c.id}`}
                          >
                            <span className="font-mono text-muted-foreground mr-1.5">{c.numero}</span>
                            {c.iaTitre ?? c.titre ?? c.message.substring(0, 60)}
                          </button>
                        ))}
                      </div>
                    )}
                    {doublon && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          className="text-xs h-7"
                          onClick={() => lierMutation.mutate({ id: selectedItem.id, parent_id: doublon })}
                          disabled={lierMutation.isPending}
                          data-testid="btn-lier-doublon"
                        >
                          {lierMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                          Marquer comme doublon
                        </Button>
                        <button onClick={() => { setDoublon(""); setDoublonSearch(""); }} className="text-xs text-muted-foreground hover:underline">
                          Annuler
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Link2 className="w-3.5 h-3.5" />
                    Doublon de {selectedItem.demandeParenteId}
                    <button
                      className="text-primary hover:underline"
                      onClick={() => lierMutation.mutate({ id: selectedItem.id, parent_id: null })}
                      data-testid="btn-delier"
                    >
                      Délier
                    </button>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setSelectedId(null)} data-testid="btn-cancel-detail">
                    Fermer
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saveMutation.isPending} data-testid="btn-save-detail">
                    {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                    Enregistrer
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
